import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Modal, Spin, Table } from "antd";
import Button from "../../../../../components/functional_components/Button/PFButton";
import RDA from "../../../../../services/api/RDA";
import { get, toLower } from "lodash";
import { showError, formatNumber } from "../../../../../utils/utilities";
import { dbDate, langDate, paginationDefaults } from "../../../../../utils/properties";
import dayjs from "dayjs";

const RdaDetailModal = ({ modalState, setModalState }) => {
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langDate[toLower(currentLanguage)];

  const [state, setState] = useState({
    loading: true,
    data: [],
    columns: [],
    isExpanded: false,
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
  });

  useEffect(() => {
    if (modalState.open && (modalState.projectTaskId || modalState.projectId)) {
      getRdaData();
    }
  }, [modalState.open, modalState.projectId, modalState.projectTaskId, modalState.serviceId]);

  useEffect(() => {
    getColumns();
  }, [state.isExpanded]);

  const getRdaData = async (page = state.pagination.currentPage) => {
    setState((prev) => ({ ...prev, loading: true }));
    const queryString = `page=${page - 1}&size=${state.pagination.size}&sort=creationDate;desc`;

    const detail = modalState.serviceId
      ? await RDA.getDetailByServiceId(
          modalState.projectId,
          modalState.projectTaskId,
          modalState.serviceId,
          queryString
        )
      : await RDA.getDetail(modalState.projectTaskId, queryString);
    if (!detail.error) {
      const data = get(detail, "data", []);
      data.forEach((item, i) => {
        // to remove console warning, set this unique constraint as the key
        item.key = `${item.rdaId}-${item.positionId || i}-${item.purchaseOrderId}-${item.purchaseOrderPositionId}`;
      });
      setState((prev) => ({
        ...prev,
        data,
        pagination: {
          currentPage: detail.currentPage ? detail.currentPage + 1 : paginationDefaults.page,
          size: get(detail, "pageSize", paginationDefaults.size),
          totalItems: get(detail, "totalItems", paginationDefaults.totalItems),
        },
      }));
      getColumns();
    } else {
      showError(get(detail, "error.messageCode", "ER000"));
    }
    setState((prev) => ({ ...prev, loading: false }));
  };

  const getColumns = () => {
    const renderAmount = (amount) =>
      amount === null ? "-" : formatNumber({ value: amount, suffix: "EUR", lang: currentLanguage, decimalScale: 0 });

    const renderAmountNotNegative = (amount) =>
      amount === null
        ? "-"
        : formatNumber({ value: Math.max(0, amount), suffix: "EUR", lang: currentLanguage, decimalScale: 0 });

    const renderQuantity = (quantity) =>
      quantity === null
        ? "-"
        : formatNumber({
            value: quantity,
            lang: currentLanguage,
            decimalScale: 0,
          });

    const renderDeliveredQuantity = (quantity) => {
      if (quantity === null) {
        return "-";
      }
      const numericQuantity = Number(quantity);
      const hasDecimals = numericQuantity % 1 !== 0;
      return formatNumber({
        value: numericQuantity,
        lang: currentLanguage,
        decimalScale: hasDecimals ? 2 : 0,
      });
    };

    const renderPurchaseOrder = (purchaseOrderId, record) => {
      const position = record?.purchaseOrderPositionId;
      if (position !== null && position !== undefined && position !== "") {
        return `${purchaseOrderId}/${position}`;
      }
      return purchaseOrderId;
    };

    const renderRda = (rdaId, record) => {
      const position = record?.positionId;
      if (position !== null && position !== undefined && position !== "") {
        return `${rdaId}/${position}`;
      }
      return rdaId;
    };

    const objCol = [
      {
        title: t("projectsManagement.planning.rdaDetail.rdaDate"),
        dataIndex: "creationDate",
        width: 100,
        render: (date) => date !== null && dayjs(date, dbDate).format(dateFormat),
      },
      {
        title: t("projectsManagement.planning.rdaDetail.cartId"),
        dataIndex: "purchaseCartId",
        width: 100,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.rdaId"),
        dataIndex: "rdaId",
        width: 100,
        render: renderRda,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.oda"),
        dataIndex: "purchaseOrderId",
        width: 100,
        render: renderPurchaseOrder,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.positionDescription"),
        dataIndex: "positionDescription",
        width: 100,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.sellerCompanyName"),
        dataIndex: "sellerCompanyName",
        width: 100,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.odaStatus"),
        dataIndex: "purchaseOrderStatusText",
        width: 100,
        hidden: !state.isExpanded,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.productCategory"),
        dataIndex: "productCategoryId",
        width: 100,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.committedValue"),
        dataIndex: "committedValue",
        width: 100,
        hidden: state.isExpanded,
        className: "table-value-right",
        render: renderAmount,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.committedValue"),
        hidden: !state.isExpanded,
        children: [
          {
            title: t("projectsManagement.planning.rdaDetail.committedRequested"),
            dataIndex: "amountRequested",
            width: 100,
            className: "table-value-right",
            render: renderAmount,
          },
          {
            title: t("projectsManagement.planning.rdaDetail.committedOrdered"),
            dataIndex: "amountOrdered",
            width: 100,
            className: "table-value-right",
            render: renderAmount,
          },
        ],
      },
      {
        title: t("projectsManagement.planning.rdaDetail.incurredCost"),
        dataIndex: "cost",
        width: 100,
        hidden: state.isExpanded,
        className: "table-value-right",
        render: renderAmount,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.incurredCost"),
        hidden: !state.isExpanded,
        children: [
          {
            title: t("projectsManagement.planning.rdaDetail.invoicedValue"),
            dataIndex: "amountInvoice",
            width: 100,
            className: "table-value-right",
            render: renderAmount,
          },
          {
            title: t("projectsManagement.planning.rdaDetail.deliveredValue"),
            dataIndex: "amountDelivered",
            width: 100,
            className: "table-value-right",
            render: renderAmount,
          },
        ],
      },
      {
        title: t("projectsManagement.planning.rdaDetail.residual"),
        dataIndex: "residualCommittedValue",
        width: 100,
        hidden: state.isExpanded,
        className: "table-value-right",
        render: renderAmountNotNegative,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.residual"),
        hidden: !state.isExpanded,
        children: [
          {
            title: t("projectsManagement.planning.rdaDetail.residualCommitted"),
            dataIndex: "residualCommittedValue",
            width: 100,
            className: "table-value-right",
            render: renderAmountNotNegative,
          },
        ],
        onHeaderCell: () => ({ style: { textAlign: "center" } }),
      },
      {
        title: t("projectsManagement.planning.rdaDetail.requestedQuantity"),
        dataIndex: "quantityRequested",
        width: 80,
        hidden: !state.isExpanded,
        className: "table-value-center",
        render: renderQuantity,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.orderedQuantity"),
        dataIndex: "quantityOrdered",
        width: 80,
        hidden: !state.isExpanded,
        className: "table-value-center",
        render: renderQuantity,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.deliveredQuantity"),
        dataIndex: "quantityDelivered",
        width: 80,
        hidden: !state.isExpanded,
        className: "table-value-center",
        render: renderDeliveredQuantity,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.activity"),
        dataIndex: "projectTaskId",
        width: 130,
      },
      {
        title: t("projectsManagement.planning.rdaDetail.extraBudget"),
        dataIndex: "isExtraBudget",
        width: 80,
        className: "table-value-center",
        render: (isExtraBudget) => (isExtraBudget ? t("general.yes") : t("general.no")),
      },
    ];
    setState((prev) => ({ ...prev, columns: objCol }));
  };

  const toggleTableExpansion = () => {
    setState((prev) => ({ ...prev, isExpanded: !prev.isExpanded }));
  };

  const closeModal = () => {
    setModalState({ open: false, projectTaskId: null });
    setState({
      loading: false,
      data: [],
      columns: [],
      isExpanded: false,
      pagination: {
        currentPage: paginationDefaults.page,
        size: paginationDefaults.size,
        totalItems: paginationDefaults.totalItems,
      },
    });
  };

  return (
    <Modal
      width="90%"
      title={<b>{t("projectsManagement.planning.rdaDetail.title")}</b>}
      open={modalState.open}
      onCancel={() => closeModal()}
      footer={null}
      destroyOnHidden
    >
      {state.loading ? (
        <div className="container-spinner-positioning">
          <Spin spinning={state.loading} className="spinner-positioning" />
        </div>
      ) : (
        ""
      )}
      <Table
        bordered
        columns={state.columns}
        dataSource={state.data}
        rowKey={"key"}
        rowHoverable={false}
        rowClassName={(record) =>
          record.annulmentStatusId && record.annulmentStatusId === "ANNULLED" ? "annulledRda" : ""
        }
        components={{
          body: {
            row: ({ children, ...props }) => (
              <tr
                {...props}
                title={
                  props.className && props.className.includes("annulledRda")
                    ? t("projectsManagement.planning.rdaDetail.annulled")
                    : ""
                }
              >
                {children}
              </tr>
            ),
          },
        }}
        size="small"
        scroll={{ x: "max-content" }}
        className="mt-10"
        pagination={{
          size: "small",
          showSizeChanger: false,
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            getRdaData(page);
          },
        }}
      />
      {state.data.length > 0 && (
        <Button
          content={
            state.isExpanded
              ? t("projectsManagement.planning.rdaDetail.collapseTable")
              : t("projectsManagement.planning.rdaDetail.expandTable")
          }
          clickCallback={toggleTableExpansion}
          className="justify-center"
        />
      )}
    </Modal>
  );
};

export default RdaDetailModal;
